import { Camera, Image as ImageIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Label } from '@/components/ui/label'

const ACCEPTED = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg']
const MAX_MB = 5

interface CarrierLogoUploadProps {
  label: string
  /** The logo already on the record, shown until a new file is staged. */
  currentUrl?: string | null
  /** The staged file, owned by the form so its Save can send it. */
  file: File | null
  onChange: (file: File | null) => void
  /** Where this logo is used, shown under the tile. */
  hint: React.ReactNode
}

/** Click-the-logo upload for a carrier's logo, mirroring the staff photo card. */
export function CarrierLogoUpload({ label, currentUrl, file, onChange, hint }: CarrierLogoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)

  // Object URLs leak until revoked, and one is created per pick.
  useEffect(() => {
    if (!file) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  function handlePick(event: React.ChangeEvent<HTMLInputElement>) {
    const picked = event.target.files?.[0]
    // Reset first: without it, re-picking the same file fires no change event.
    event.target.value = ''
    if (!picked) return

    if (!ACCEPTED.some((ext) => picked.name.toLowerCase().endsWith(ext))) {
      toast.error(`Unsupported image type. Use one of: ${ACCEPTED.join(', ')}.`)
      return
    }
    if (picked.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image must be ${MAX_MB} MB or smaller.`)
      return
    }
    onChange(picked)
  }

  const shown = preview ?? currentUrl
  const action = shown ? `Change the ${label.toLowerCase()}` : `Add a ${label.toLowerCase()}`

  return (
    <div className="flex flex-col gap-2">
      <Label>{label}</Label>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        aria-label={action}
        title={action}
        className="group focus-visible:ring-ring/50 bg-background relative size-20 shrink-0 cursor-pointer overflow-hidden rounded-md border outline-none focus-visible:ring-[3px]"
      >
        {shown ? (
          <img src={shown} alt="" className="size-full object-contain p-1" />
        ) : (
          <span className="text-muted-foreground flex size-full items-center justify-center">
            <ImageIcon className="size-8" />
          </span>
        )}
        {/* Nothing about a logo says "clickable", so reveal the affordance on hover and keyboard focus. */}
        <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          <Camera className="size-6 text-white" />
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        onChange={handlePick}
        className="hidden"
        aria-hidden="true"
      />

      <p className="text-muted-foreground text-xs">
        {file ? (
          `${file.name} — save to apply`
        ) : (
          <>
            {hint} Click to {shown ? 'replace' : 'add'} — PNG, JPG, GIF, WEBP or SVG, max {MAX_MB} MB.
          </>
        )}
      </p>
    </div>
  )
}
